import { useState, useEffect } from 'react';
import { FiX, FiRefreshCw } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { getRandomCategories, getUserCategories, updateUserCategories } from '../utils/api';

/**
 * 获取本地化类别标签
 * @param {object} category - 类别对象
 * @param {string} lang - 语言代码
 * @returns {string} 本地化标签
 */
function getLocalizedLabel(category, lang = 'zh') {
  if (!category.i18n) {
    return category.label;
  }

  // 根据语言返回本地化标签
  if (lang === 'zh' && category.i18n.zh_CN) {
    return category.i18n.zh_CN;
  }
  if (lang === 'en' && category.i18n.en) {
    return category.i18n.en;
  }

  // 默认返回原始标签
  return category.label;
}

/**
 * 兴趣类别选择模态框组件
 */
export function CategorySelectionModal({ open, onClose, onSave }) {
  const { userId } = useAuth();
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // 获取浏览器语言
  const browserLanguage = navigator.language || navigator.userLanguage || 'zh';
  const language = browserLanguage.startsWith('zh') ? 'zh' : 'en';

  // 根据当前选择获取随机类别，确保总数为16且保留已选
  const fetchCategories = async (currentSelected = []) => {
    setLoading(true);
    setError('');
    try {
      // 需要的随机数量 = 16 - 已选数量（不小于0）
      const need = Math.max(16 - (currentSelected?.length || 0), 0);
      console.log('[CategoryModal] 获取随机类别, need:', need, 'currentSelected:', currentSelected);
      const data = await getRandomCategories(need);

      if (data.code === 0) {
        const fetched = Array.isArray(data.data) ? data.data : [];
        // 合并：已选 + 新批次（去重, 以 slug 为键）
        const selectedMap = new Map((currentSelected || []).map(c => [c.slug, c]));
        const merged = [
          ...(currentSelected || []),
          ...fetched.filter(c => !selectedMap.has(c.slug)),
        ];
        // 只保留最多16个
        const final = merged.slice(0, 16);
        console.log('[CategoryModal] 设置类别列表:', final);
        setCategories(final);
      } else {
        setError('获取类别列表失败');
      }
    } catch (err) {
      console.error('获取类别失败:', err);
      setError('网络错误，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 获取用户已选择的类别
  const fetchUserCategories = async () => {
    if (!userId) return;

    try {
      console.log('[CategoryModal] 开始获取用户类别, userId:', userId);
      const data = await getUserCategories(userId);
      console.log('[CategoryModal] 获取用户类别响应:', data);

      if (data.code === 0) {
        setSelectedCategories(data.data || []);
        console.log('[CategoryModal] 已设置选中类别:', data.data);
      }
    } catch (err) {
      console.error('获取用户类别失败:', err);
    }
  };

  // 初始化时获取类别列表
  useEffect(() => {
    if (!open) return;
    console.log('[CategoryModal] 打开弹窗，userId:', userId);
    let cancelled = false;
    (async () => {
      // 先加载用户已选
      await fetchUserCategories();

      // 等待状态更新后再加载候选类别
      // 由于 setSelectedCategories 是异步的，我们需要直接从 API 响应获取数据
      if (!cancelled) {
        try {
          const data = await getUserCategories(userId);
          const userSelected = (data.code === 0 && data.data) ? data.data : [];
          await fetchCategories(userSelected);
        } catch (err) {
          console.error('重新获取用户类别失败:', err);
          await fetchCategories([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, userId]);

  // 切换类别选择
  const toggleCategory = (category) => {
    const isSelected = selectedCategories.some(c => c.slug === category.slug);

    if (isSelected) {
      // 取消选择
      setSelectedCategories(prev => prev.filter(c => c.slug !== category.slug));
    } else {
      // 添加选择（最多5个）
      if (selectedCategories.length < 5) {
        setSelectedCategories(prev => [...prev, category]);
      }
    }
  };

  // 保存选择
  const handleSave = async () => {
    if (!userId) {
      setError('用户未登录');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const data = await updateUserCategories(
        userId,
        selectedCategories.map(c => c.slug)
      );

      if (data.code === 0) {
        onSave?.(selectedCategories);
        onClose();
      } else {
        setError(data.msg || '保存失败');
      }
    } catch (err) {
      console.error('保存类别失败:', err);
      setError('网络错误，请重试');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-slate-950/75 backdrop-blur-[18px] flex items-center justify-center p-6 z-[100]"
      role="dialog"
      aria-modal="true"
    >
      <div className="glass-xl w-full max-w-[640px] p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <p className="uppercase tracking-[0.3em] text-[0.6rem] md:text-[0.65rem] text-text-muted">
              个性化设置
            </p>
            <h2 className="text-xl md:text-2xl mt-1">选择你的兴趣</h2>
          </div>
          <button
            className="ghost-pill w-9 h-9 inline-flex items-center justify-center bg-slate-900/60 border border-slate-600/60 text-text rounded-3xl cursor-pointer transition-all duration-200 hover:border-accent hover:text-sky-100"
            onClick={onClose}
            aria-label="关闭类别选择弹窗"
          >
            <FiX />
          </button>
        </div>

        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <p className="text-slate-300/70 text-sm md:text-[0.95rem] leading-relaxed">
              选择你感兴趣的类别，帮助我们为你推荐更相关的内容
            </p>
            <button
              onClick={() => fetchCategories(selectedCategories)}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-800/80 hover:bg-slate-700/80 text-text text-sm transition-all duration-200 disabled:opacity-50"
            >
              <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              换一批
            </button>
          </div>

          {error && (
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {categories.map((category) => {
                const isSelected = selectedCategories.some(c => c.slug === category.slug);
                const localizedLabel = getLocalizedLabel(category, language);

                return (
                  <button
                    key={category.slug}
                    onClick={() => toggleCategory(category)}
                    disabled={!isSelected && selectedCategories.length >= 5}
                    className={`px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 border-2 ${
                      isSelected
                        ? 'bg-sky-500/20 border-sky-500 text-sky-300'
                        : selectedCategories.length >= 5
                        ? 'bg-slate-800/50 border-slate-700/50 text-slate-500 cursor-not-allowed'
                        : 'bg-slate-800/60 hover:bg-slate-700/60 border-slate-600/60 text-text hover:border-slate-500'
                    }`}
                  >
                    <div className="flex items-center justify-center">
                      {isSelected && (
                        <div className="w-2 h-2 rounded-full bg-sky-500 mr-2"></div>
                      )}
                      <span>{localizedLabel}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-400">
              已选择 {selectedCategories.length}/5 个类别
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-6 py-3 rounded-xl bg-slate-800/80 hover:bg-slate-700/80 text-text text-sm font-medium transition-all duration-200"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                disabled={saving || selectedCategories.length === 0}
                className="px-6 py-3 rounded-xl bg-sky-600 hover:bg-sky-500 text-white text-sm font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>

          <div className="text-xs text-slate-500 text-center leading-relaxed">
            选择后可随时在设置中修改你的兴趣偏好
          </div>
        </div>
      </div>
    </div>
  );
}
